import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { 
  Calendar, 
  Clock, 
  Plus, 
  Edit, 
  Trash2, 
  Save, 
  X, 
  BookOpen,
  Users,
  MapPin,
  Video,
  ChevronLeft,
  Layers
} from 'lucide-react';
import teacherService from '../../services/api/teacher';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { useToast } from '../../components/common/Toast';

const ManageSchedule = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();

  const [selectedDay, setSelectedDay] = useState(1); // Default: الإثنين
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState(null);
  const [formData, setFormData] = useState({
    courseId: '',
    lessonTitle: '',
    lessonDescription: '',
    dayOfWeek: selectedDay,
    startTime: '',
    endTime: '',
    classroom: '',
    meetingLink: '',
    isRecurring: false,
    notes: ''
  });

  const weekDays = [
    { id: 0, name: 'الأحد' },
    { id: 1, name: 'الإثنين' },
    { id: 2, name: 'الثلاثاء' },
    { id: 3, name: 'الأربعاء' },
    { id: 4, name: 'الخميس' },
    { id: 5, name: 'الجمعة' },
    { id: 6, name: 'السبت' }
  ];

  const timeSlots = [
    '08:00', '09:00', '10:00', '11:00', '12:00',
    '13:00', '14:00', '15:00', '16:00', '17:00', '18:00', '19:00', '20:00'
  ];

  // Fetch schedule data
  const { data: scheduleData, isLoading } = useQuery(
    ['teacher-schedule', 'current'],
    () => teacherService.getTeacherSchedule(),
    { enabled: true }
  );

  // Fetch available slots for selected day
  const { data: availableSlots } = useQuery(
    ['available-slots', selectedDay],
    () => teacherService.getAvailableSlots({ dayOfWeek: selectedDay }),
    { enabled: true }
  );

  // Fetch teacher's courses
  const { data: coursesData } = useQuery(
    'teacher-courses',
    () => teacherService.getTeacherCourses(),
    { enabled: true }
  );

  // Create schedule mutation
  const createScheduleMutation = useMutation(
    (data) => teacherService.createSchedule(data),
    {
      onSuccess: () => {
        toast.success('تم إضافة الحصة للجدول بنجاح');
        setShowAddForm(false);
        setFormData({
          courseId: '',
          lessonTitle: '',
          lessonDescription: '',
          dayOfWeek: selectedDay,
          startTime: '',
          endTime: '',
          classroom: '',
          meetingLink: '',
          isRecurring: false,
          notes: ''
        });
        queryClient.invalidateQueries('teacher-schedule');
        queryClient.invalidateQueries('available-slots');
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'فشل في إضافة الحصة');
      }
    }
  );

  // Update schedule mutation
  const updateScheduleMutation = useMutation(
    ({ id, data }) => teacherService.updateSchedule(id, data),
    {
      onSuccess: () => {
        toast.success('تم تحديث الحصة بنجاح');
        setEditingSchedule(null);
        queryClient.invalidateQueries('teacher-schedule');
        queryClient.invalidateQueries('available-slots');
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'فشل في تحديث الحصة');
      }
    }
  );

  // Delete schedule mutation
  const deleteScheduleMutation = useMutation(
    (id) => teacherService.deleteSchedule(id),
    {
      onSuccess: () => {
        toast.success('تم حذف الحصة بنجاح');
        queryClient.invalidateQueries('teacher-schedule');
        queryClient.invalidateQueries('available-slots');
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'فشل في حذف الحصة');
      }
    }
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (editingSchedule) {
      updateScheduleMutation.mutate({
        id: editingSchedule.schedule_id,
        data: formData
      });
    } else {
      createScheduleMutation.mutate(formData);
    }
  };

  const handleEdit = (schedule) => {
    setEditingSchedule(schedule);
    setFormData({
      courseId: schedule.course_id,
      lessonTitle: schedule.lesson_title,
      lessonDescription: schedule.lesson_description || '',
      dayOfWeek: schedule.day_of_week,
      startTime: schedule.start_time,
      endTime: schedule.end_time,
      classroom: schedule.classroom || '',
      meetingLink: schedule.meeting_link || '',
      isRecurring: schedule.is_recurring,
      notes: schedule.notes || ''
    });
    setShowAddForm(true);
  };

  const handleDelete = (scheduleId) => {
    if (window.confirm('هل أنت متأكد من حذف هذه الحصة؟')) {
      deleteScheduleMutation.mutate(scheduleId);
    }
  };

  const filteredLessons = scheduleData?.lessons?.filter(
    lesson => lesson.day_of_week === selectedDay
  ) || [];

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-24 min-h-[500px]">
        <LoadingSpinner size="large" />
        <p className="mt-4 text-gray-500 font-bold animate-pulse uppercase tracking-widest text-xs">جاري تحميل الجدول الزمني...</p>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-8 px-4 sm:px-6 py-6 min-h-screen bg-gray-50/30">
      
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
        <div className="space-y-1">
          <h1 className="text-3xl lg:text-4xl font-black text-gray-900 tracking-tight flex items-center gap-3">
            <Calendar className="text-primary-600 bg-primary-50 p-1.5 rounded-xl" size={36} />
            إدارة الجدول الزمني
          </h1>
          <p className="text-gray-500 font-medium flex items-center gap-2 mr-12">
            <Layers className="text-emerald-500" size={18} />
            إضافة وتعديل وحذف الحصص الدراسية المجدولة
          </p>
        </div>
        
        <button
          onClick={() => navigate('/teacher/schedule')}
          className="flex items-center gap-2 text-primary-600 hover:text-primary-700 font-medium"
        >
          <ChevronLeft size={20} />
          العودة للجدول
        </button>
      </div>

      {/* Day Selector */}
      <div className="bg-white rounded-[2rem] shadow-sm border border-gray-100 p-6">
        <div className="flex flex-wrap gap-3">
          {weekDays.map((day) => (
            <button
              key={day.id}
              onClick={() => {
                setSelectedDay(day.id);
                setFormData(prev => ({ ...prev, dayOfWeek: day.id }));
              }}
              className={`px-6 py-3 rounded-xl font-bold transition-all ${
                selectedDay === day.id
                  ? 'bg-primary-600 text-white shadow-lg'
                  : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
              }`}
            >
              {day.name}
            </button>
          ))}
        </div>
      </div>

      {/* Add/Edit Form */}
      {showAddForm && (
        <div className="bg-white rounded-[2rem] shadow-sm border border-gray-100 p-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-black text-gray-900">
              {editingSchedule ? 'تعديل حصة' : 'إضافة حصة جديدة'}
            </h2>
            <button
              onClick={() => {
                setShowAddForm(false);
                setEditingSchedule(null);
                setFormData({
                  lessonId: '',
                  dayOfWeek: selectedDay,
                  startTime: '',
                  endTime: '',
                  classroom: '',
                  meetingLink: '',
                  isRecurring: false,
                  notes: ''
                });
              }}
              className="p-2 text-gray-400 hover:text-gray-600"
            >
              <X size={24} />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Course Selection */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  اختر الكورس
                </label>
                <select
                  value={formData.courseId}
                  onChange={(e) => setFormData(prev => ({ ...prev, courseId: e.target.value }))}
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-100 rounded-xl focus:ring-4 focus:ring-primary-500/10 focus:bg-white"
                  required
                >
                  <option value="">اختر كورساً...</option>
                  {coursesData?.courses?.map((course) => (
                    <option key={course.id} value={course.id}>
                      {course.title}
                    </option>
                  ))}
                </select>
              </div>

              {/* Lesson Title */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  عنوان الحصة
                </label>
                <input
                  type="text"
                  value={formData.lessonTitle}
                  onChange={(e) => setFormData(prev => ({ ...prev, lessonTitle: e.target.value }))}
                  placeholder="مثال: مقدمة في الجبر"
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-100 rounded-xl focus:ring-4 focus:ring-primary-500/10 focus:bg-white"
                  required
                />
              </div>

              {/* Lesson Description */}
              <div className="md:col-span-2">
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  وصف الحصة (اختياري)
                </label>
                <textarea
                  value={formData.lessonDescription}
                  onChange={(e) => setFormData(prev => ({ ...prev, lessonDescription: e.target.value }))}
                  placeholder="وصف موجز لمحتوى الحصة..."
                  rows={3}
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-100 rounded-xl focus:ring-4 focus:ring-primary-500/10 focus:bg-white"
                />
              </div>

              {/* Time Selection */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">
                    وقت البداية
                  </label>
                  <select
                    value={formData.startTime}
                    onChange={(e) => setFormData(prev => ({ ...prev, startTime: e.target.value }))}
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-100 rounded-xl focus:ring-4 focus:ring-primary-500/10 focus:bg-white"
                    required
                  >
                    <option value="">اختر الوقت</option>
                    {timeSlots.map((time) => (
                      <option key={time} value={time}>{time}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">
                    وقت النهاية
                  </label>
                  <select
                    value={formData.endTime}
                    onChange={(e) => setFormData(prev => ({ ...prev, endTime: e.target.value }))}
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-100 rounded-xl focus:ring-4 focus:ring-primary-500/10 focus:bg-white"
                    required
                  >
                    <option value="">اختر الوقت</option>
                    {timeSlots.map((time) => (
                      <option key={time} value={time}>{time}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Classroom */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  القاعة الدراسية
                </label>
                <input
                  type="text"
                  value={formData.classroom}
                  onChange={(e) => setFormData(prev => ({ ...prev, classroom: e.target.value }))}
                  placeholder="مثال: قاعة 101"
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-100 rounded-xl focus:ring-4 focus:ring-primary-500/10 focus:bg-white"
                />
              </div>

              {/* Meeting Link */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  رابط الاجتماع (اختياري)
                </label>
                <input
                  type="url"
                  value={formData.meetingLink}
                  onChange={(e) => setFormData(prev => ({ ...prev, meetingLink: e.target.value }))}
                  placeholder="https://zoom.us/..."
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-100 rounded-xl focus:ring-4 focus:ring-primary-500/10 focus:bg-white"
                />
              </div>
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">
                ملاحظات
              </label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="ملاحظات إضافية عن الحصة..."
                rows={3}
                className="w-full px-4 py-3 bg-gray-50 border border-gray-100 rounded-xl focus:ring-4 focus:ring-primary-500/10 focus:bg-white"
              />
            </div>

            {/* Recurring Option */}
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="isRecurring"
                checked={formData.isRecurring}
                onChange={(e) => setFormData(prev => ({ ...prev, isRecurring: e.target.checked }))}
                className="w-5 h-5 text-primary-600 bg-gray-50 border-gray-100 rounded focus:ring-primary-500"
              />
              <label htmlFor="isRecurring" className="text-sm font-bold text-gray-700">
                حصة متكررة كل أسبوع
              </label>
            </div>

            {/* Submit Buttons */}
            <div className="flex items-center gap-4">
              <button
                type="submit"
                disabled={createScheduleMutation.isLoading || updateScheduleMutation.isLoading}
                className="flex items-center gap-2 bg-primary-600 text-white px-8 py-3 rounded-xl font-black shadow-lg hover:bg-primary-700 transition-all disabled:opacity-50"
              >
                <Save size={20} />
                {editingSchedule ? 'تحديث الحصة' : 'إضافة الحصة'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowAddForm(false);
                  setEditingSchedule(null);
                }}
                className="flex items-center gap-2 bg-gray-100 text-gray-600 px-8 py-3 rounded-xl font-bold hover:bg-gray-200 transition-all"
              >
                <X size={20} />
                إلغاء
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Add Button */}
      {!showAddForm && (
        <div className="flex justify-center">
          <button
            onClick={() => setShowAddForm(true)}
            className="flex items-center gap-3 bg-primary-600 text-white px-8 py-4 rounded-2xl font-black shadow-xl hover:bg-primary-700 transition-all"
          >
            <Plus size={24} />
            إضافة حصة جديدة ليوم {weekDays[selectedDay].name}
          </button>
        </div>
      )}

      {/* Schedule List */}
      <div className="space-y-4">
        {filteredLessons.length > 0 ? (
          filteredLessons.map((lesson) => (
            <div key={lesson.schedule_id} className="bg-white rounded-[2rem] shadow-sm border border-gray-100 p-6 hover:shadow-lg transition-all">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                <div className="flex-1 space-y-4">
                  <div className="flex items-start gap-4">
                    <div className="p-3 bg-primary-50 text-primary-600 rounded-xl">
                      <BookOpen size={24} />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-black text-gray-900">{lesson.lesson_title}</h3>
                      <p className="text-sm text-gray-600">{lesson.course_title}</p>
                      <p className="text-xs text-primary-600 font-bold mt-1">{lesson.subject_name}</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="flex items-center gap-2 text-sm">
                      <Clock size={16} className="text-gray-400" />
                      <span className="font-bold text-gray-700">{lesson.time}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <MapPin size={16} className="text-gray-400" />
                      <span className="font-bold text-gray-700">{lesson.classroom || 'غير محدد'}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <Users size={16} className="text-gray-400" />
                      <span className="font-bold text-gray-700">{lesson.enrolled_students} طالب</span>
                    </div>
                    {lesson.meeting_link && (
                      <div className="flex items-center gap-2 text-sm">
                        <Video size={16} className="text-gray-400" />
                        <a 
                          href={lesson.meeting_link} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-primary-600 hover:underline font-bold"
                        >
                          انضم للحصة
                        </a>
                      </div>
                    )}
                  </div>

                  {lesson.notes && (
                    <div className="bg-gray-50 rounded-xl p-3">
                      <p className="text-sm text-gray-600">{lesson.notes}</p>
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleEdit(lesson)}
                    className="p-3 text-blue-600 hover:bg-blue-50 rounded-xl transition-colors"
                  >
                    <Edit size={20} />
                  </button>
                  <button
                    onClick={() => handleDelete(lesson.schedule_id)}
                    className="p-3 text-red-600 hover:bg-red-50 rounded-xl transition-colors"
                  >
                    <Trash2 size={20} />
                  </button>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="bg-white rounded-[2rem] shadow-sm border border-gray-100 p-12 text-center">
            <div className="flex flex-col items-center gap-4">
              <div className="p-4 bg-gray-50 rounded-full">
                <Calendar size={48} className="text-gray-400" />
              </div>
              <div>
                <h3 className="text-lg font-black text-gray-900">لا توجد حصص مجدولة</h3>
                <p className="text-gray-600 mt-2">يوم {weekDays[selectedDay].name} خالي من الحصص</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Available Slots */}
      {availableSlots?.availableSlots?.length > 0 && (
        <div className="bg-white rounded-[2rem] shadow-sm border border-gray-100 p-6">
          <h3 className="text-lg font-black text-gray-900 mb-4">الأوقات المتاحة ليوم {weekDays[selectedDay].name}</h3>
          <div className="flex flex-wrap gap-2">
            {availableSlots.availableSlots.map((slot, index) => (
              <div key={index} className="px-3 py-2 bg-green-50 text-green-700 rounded-lg text-sm font-bold">
                {slot.start} - {slot.end}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageSchedule;
